import axios from "axios";
import querystring from "querystring";

// 从URL参数获取值，不转小写（用于token和jwt）
const getQueryParam = (name) => {
  try {
    // 优先使用 URLSearchParams（更可靠）
    const urlParams = new URLSearchParams(window.location.search);
    const value = urlParams.get(name);
    if (value) {
      return value;
    }
    
    // 备用方案：使用正则表达式（处理URLSearchParams无法处理的长值）
    // 注意：JWT值可能很长，需要匹配到下一个&或字符串结尾
    var reg = new RegExp("(^|&)" + name + "=([^&]*)", "i");
    var r = window.location.search.substr(1).match(reg);
    if (r != null && r[2]) {
      return decodeURIComponent(r[2]);
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// 保存 token 到 sessionStorage
const saveTokenToStorage = (token) => {
  try {
    if (token) {
      sessionStorage.setItem("auth_token", token);
    } else {
      sessionStorage.removeItem("auth_token");
    }
  } catch (error) {
    console.error("保存 token 到缓存失败:", error);
  }
};

// 保存 jwt 到 sessionStorage
const saveJwtToStorage = (jwt) => {
  try {
    if (jwt) {
      sessionStorage.setItem("auth_jwt", jwt);
    } else {
      sessionStorage.removeItem("auth_jwt");
    }
  } catch (error) {
    console.error("保存 jwt 到缓存失败:", error);
  }
};

const saveUserIdToStorage = (userId) => {
  try {
    if (userId) {
      sessionStorage.setItem("auth_user_id", userId);
    } else {
      sessionStorage.removeItem("auth_user_id");
    }
  } catch (error) {
    console.error("保存 user_id 到缓存失败:", error);
  }
};

// 从缓存获取 token
const getTokenFromStorage = () => {
  try {
    return sessionStorage.getItem("auth_token");
  } catch (error) {
    return null;
  }
};

// 从缓存获取 jwt
const getJwtFromStorage = () => {
  try {
    return sessionStorage.getItem("auth_jwt");
  } catch (error) {
    return null;
  }
};

const getUserIdFromStorage = () => {
  try {
    return sessionStorage.getItem("auth_user_id");
  } catch (error) {
    return null;
  }
};

const getUserIdFromPath = () => {
  try {
    const match = window.location.pathname.match(
      /^\/(?:file_recognition|preview_words|analysis_article|create_book_name|add-words|editor_book)\/([^/]+)/
    );
    return match && match[1] ? decodeURIComponent(match[1]) : null;
  } catch (error) {
    return null;
  }
};

// 获取token的函数：优先从缓存读取，如果没有则从URL读取并保存到缓存
const getToken = () => {
  // 优先从缓存读取
  let token = getTokenFromStorage();
  
  // 如果缓存中没有，尝试从URL读取
  const urlToken = getQueryParam("token");
  if (urlToken) {
    // 如果 URL 中有 token，更新缓存（确保使用最新的）
    if (urlToken !== token) {
      saveTokenToStorage(urlToken);
    }
    return urlToken;
  }
  
  // 如果 URL 中也没有，返回缓存中的值（如果有）
  return token;
};

// 获取jwt的函数：优先从缓存读取，如果没有则从URL读取并保存到缓存
const getJwt = () => {
  const urlJwt = getQueryParam("jwt");
  const urlToken = getQueryParam("token");
  if (urlJwt) {
    const cached = getJwtFromStorage();
    if (urlJwt !== cached) {
      saveJwtToStorage(urlJwt);
    }
    return urlJwt;
  }
  // 当前页只有 App token 时，不用上次缓存的 jwt
  if (urlToken) {
    return null;
  }
  return getJwtFromStorage();
};

const getUserId = () => {
  const urlUserId = getQueryParam("user_id") || getUserIdFromPath();
  if (urlUserId) {
    const cached = getUserIdFromStorage();
    if (urlUserId !== cached) {
      saveUserIdToStorage(urlUserId);
    }
    return urlUserId;
  }
  return getUserIdFromStorage();
};

export const getAuthHeaders = () => {
  const headers = {};
  const jwt = getJwt();
  const token = getToken();
  const userId = getUserId();

  if (jwt) {
    headers["Authorization"] = `Bearer ${jwt}`;
  } else if (token) {
    // 只设一次 token。再设 Token 时，浏览器会合成 "xxx, xxx"，后端按整串查 Redis 会 miss
    headers["token"] = token;
  }
  if (userId) {
    headers["user_id"] = userId;
    headers["towords_id"] = userId;
  }
  headers["X-Client"] = "towords-book-mobile";
  return headers;
};

// 初始化：从URL读取 token 和 jwt 并保存到缓存（如果存在）
// 每次调用都会刷新缓存中的 token，确保使用最新的 token
const initAuthFromUrl = () => {
  const urlToken = getQueryParam("token");
  const urlJwt = getQueryParam("jwt");
  const urlUserId = getQueryParam("user_id") || getUserIdFromPath();
  
  // 如果 URL 中有 token，更新缓存（即使缓存中已有，也要更新，确保使用最新的）
  if (urlToken) {
    saveTokenToStorage(urlToken);
  }
  // 如果 URL 中有 jwt，更新缓存（jwt 优先级更高）
  if (urlJwt) {
    saveJwtToStorage(urlJwt);
    // 如果有 jwt，通常不需要 token，但为了兼容性，保留 token
  } else if (urlToken) {
    // App 只带 token 打开时，清掉上次留下的 jwt，避免旧 jwt 抢鉴权
    saveJwtToStorage(null);
  }
  if (urlUserId) {
    saveUserIdToStorage(urlUserId);
  }
};

// 页面加载时自动初始化
if (typeof window !== "undefined") {
  initAuthFromUrl();
}

// 导出刷新 token 的函数，供其他模块调用（如首页）
export const refreshAuthFromUrl = () => {
  initAuthFromUrl();
};

// 导出获取认证类型的方法，供其他模块判断当前使用的认证方式
export const getAuthType = () => {
  const jwt = getJwt();
  const token = getToken();
  
  if (jwt) {
    return 'jwt';
  } else if (token) {
    return 'token';
  }
  return null;
};

// 导出获取 token 和 jwt 的函数，供其他模块使用
export { getToken, getJwt, getUserId };

/** 线上走 /towords_book_java，由 Nginx 转到 bc.towords.net 的 /towords_book。本地开发仍用 /towords_book。 */
export const bookApiUrl = (url) => {
  if (process.env.NODE_ENV !== "production" || typeof url !== "string") {
    return url;
  }
  if (url.startsWith("/towords_book_java")) {
    return url;
  }
  if (url.startsWith("/towords_book")) {
    return "/towords_book_java" + url.slice("/towords_book".length);
  }
  return url;
};

const Http = axios.create({
  baseURL: "", //api host：api的请求基础地质
  timeout: 1000 * 60 * 10, // 超时时间ms
  withCredentials: false,
  // headers: {
  //   "Content-Type": "application/json; charset=utf-8", // 默认设置为JSON
  // },
});

// 添加请求拦截器，自动在请求头中添加token和jwt
Http.interceptors.request.use(
  (config) => {
    config.url = bookApiUrl(config.url);
    config.headers = config.headers || {};
    Object.assign(config.headers, getAuthHeaders());
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.request.use((config) => {
  if (config && typeof config.url === "string") {
    config.url = bookApiUrl(config.url);
  }
  return config;
});

// 添加响应拦截器，处理401错误
Http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      const jwt = getJwt();
      const token = getToken();
      
      // 检查JWT是否过期
      if (jwt) {
        try {
          const parts = jwt.split(".");
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            const exp = payload.exp;
            if (exp) {
              const now = Math.floor(Date.now() / 1000);
              if (now > exp) {
                console.error("[HTTP] JWT已过期", {
                  expiredAt: new Date(exp * 1000).toLocaleString(),
                  currentTime: new Date().toLocaleString(),
                });
              } else {
                console.error("[HTTP] JWT未过期，但返回401", {
                  expiresAt: new Date(exp * 1000).toLocaleString(),
                  currentTime: new Date().toLocaleString(),
                });
              }
            }
          }
        } catch (e) {
          console.error("[HTTP] 解析JWT失败", e);
        }
      }
      
      console.error("[HTTP] 401错误详情", {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        responseData: error.response?.data,
        hasJwt: !!jwt,
        hasToken: !!token,
      });
    }
    return Promise.reject(error);
  }
);
function createHttp(type) {
  return function ({ url, params, is_qs, timeout }) {
    let data = params || {};
    let headers = {};

    if (is_qs) {
      data = querystring.stringify(data);
      headers["Content-Type"] = "application/x-www-form-urlencoded";
    } else {
      headers["Content-Type"] = "application/json";
    }

    // 注意：token 和 jwt 会通过拦截器自动添加到 headers 中
    // 这里不需要手动添加，axios 会自动合并 headers

    return new Promise((resolve, reject) => {
      Http({
        url,
        data,
        method: type,
        headers,
        // 允许按请求覆盖全局超时，避免慢接口（如 doubao/chat）长时间挂起
        ...(timeout ? { timeout } : {}),
      })
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };
}
export const _post = createHttp("post");

// 统一把 axios 的成功响应 / 各类错误规整成业务可消费的对象，
// 避免超时/断网时因读取 undefined.response.data 抛出 TypeError
function normalizeResponse(data) {
  // 正常响应
  if (data && data.status == 200) {
    return data.data;
  }

  // axios 抛出的错误（超时、断网、非 2xx 等）
  const isAxiosError =
    data && (data.isAxiosError || data.code || typeof data.message === "string");

  if (isAxiosError) {
    const isTimeout =
      data.code === "ECONNABORTED" || /timeout/i.test(String(data.message || ""));
    if (isTimeout) {
      return { success: false, code: "TIMEOUT", timeout: true, msg: "请求超时，请重试" };
    }

    // 服务端返回了非 2xx，但有响应体
    if (data.response) {
      return {
        status: data.response.status,
        ...(data.response.data || {}),
      };
    }

    // 没有响应体，通常是网络异常
    return {
      success: false,
      code: "NETWORK_ERROR",
      network: true,
      msg: "网络异常，请检查网络后重试",
    };
  }

  return { success: false, msg: "请求失败" };
}
class Request {
  baseURL = "";
  constructor(baseURL) {
    this.baseURL = baseURL;
  }
  async post(url, params, options = {}) {
    let config = {
      url: `${this.baseURL ? this.baseURL : ""}${url}`,
      params,
      is_qs: true,
      timeout: options.timeout,
    };
    const data = await _post(config).catch((err) => {
      return err;
    });
    return normalizeResponse(data);
  }

  async postJson(url, params, options = {}) {
    let config = {
      url: `${this.baseURL ? this.baseURL : ""}${url}`,
      params,
      is_qs: false, // 设置为 false 以发送 JSON 数据
      timeout: options.timeout,
    };

    const data = await _post(config).catch((err) => {
      return err;
    });
    return normalizeResponse(data);
  }
}

export const $post = async (url, params, useJson = false, options = {}) => {
  if (useJson) {
    return await new Request().postJson(url, params, options);
  }
  return await new Request().post(url, params, options);
};
export const $postApi = async (url, params) => {
  return await new Request("https://api.towords.net").post(url, params);
};
export const $postApiTest = async (url, params) => {
  return await new Request("http://192.168.110.95:8080").post(url, params);
};

export const $postWx = async (url, params) => {
  return await new Request("https://wx.towords.net").post(url, params);
};

export const $postBody = async (url, params, options = {}) => {
  return await new Request().postJson(url, params, options);
};

export const $postJson = async (url, params, options = {}) => {
  return await new Request().postJson(url, params, options);
};

export const $getApi = async (url, params) => {
  const headers = getAuthHeaders();
  
  const data = await axios({
    method: "get",
    url:
      process.env.NODE_ENV === "development"
        ? url
        : `https://api.towords.net${url}`,
    params: params,
    headers: headers,
  });
  if (data.status == 200) {
    return data.data;
  } else {
    return {
      status: data.response.status,
      data: data.response.data,
    };
  }
};
